import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Grid, Paper, Button, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { differenceInDays, addYears } from 'date-fns';

export default function MortgageBreakCalculate() {
  const [currentLoanAmount, setCurrentLoanAmount] = useState<string>('600000');
  const [currentFixedExpiryDate, setCurrentFixedExpiryDate] = useState<Date | null>(addYears(new Date(), 1));
  const [currentFixedRate, setCurrentFixedRate] = useState<string>('4.99'); // Annual rate as percentage

  const [newRate, setNewRate] = useState<string>(''); // Annual rate as percentage
  const [breakFee, setBreakFee] = useState<string>('');

  const [calculationResult, setCalculationResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateBreakCostOrSaving = () => {
    setError(null);
    setCalculationResult(null);

    const loanAmount = parseFloat(currentLoanAmount);
    const oldRate = parseFloat(currentFixedRate) / 100; // Convert to decimal
    const proposedNewRate = parseFloat(newRate) / 100; // Convert to decimal
    const fee = parseFloat(breakFee) || 0; // Default to 0 if not provided

    if (isNaN(loanAmount) || loanAmount <= 0 ||
        isNaN(oldRate) || oldRate <= 0 ||
        isNaN(proposedNewRate) || proposedNewRate <= 0 ||
        !currentFixedExpiryDate) {
      setError('Please fill in all required fields with valid numbers and select an expiry date.');
      return;
    }

    const today = new Date();
    if (currentFixedExpiryDate <= today) {
      setError('The current fixed expiry date must be in the future.');
      return;
    }

    const remainingDays = differenceInDays(currentFixedExpiryDate, today);
    if (remainingDays <= 0) {
        setError('The current fixed expiry date must be in the future.');
        return;
    }

    // Simplified break cost calculation: (Old Rate - New Rate) * Loan Amount * (Remaining Days / 365)
    // This is a very simplified model. Real break fees are often more complex.
    // A common method is (Difference in wholesale rates) * Loan Amount * Remaining Term
    // Or a percentage of the loan amount, or a flat fee.
    // For this example, we'll use a simplified interest differential approach.

    const interestDifferentialPerDay = (oldRate / 365) - (proposedNewRate / 365);
    const totalInterestDifference = interestDifferentialPerDay * loanAmount * remainingDays;

    // If totalInterestDifference is positive, it means the old rate was higher, so breaking could save money on interest.
    // If negative, the new rate is higher, meaning breaking costs more in interest (unlikely scenario for a 'break and refix lower' scenario).

    const netOutcome = totalInterestDifference - fee; // Savings (if positive) or Cost (if negative)

    if (netOutcome > 0) {
      setCalculationResult(`Potential saving by breaking: $${netOutcome.toFixed(2)} (Interest saved: $${totalInterestDifference.toFixed(2)}, Break fee: $${fee.toFixed(2)})`);
    } else if (netOutcome < 0) {
      setCalculationResult(`Potential cost of breaking: $${Math.abs(netOutcome).toFixed(2)} (Interest difference: $${totalInterestDifference.toFixed(2)}, Break fee: $${fee.toFixed(2)})`);
    } else {
      setCalculationResult(`Breaking and refixing results in no net change (Interest difference: $${totalInterestDifference.toFixed(2)}, Break fee: $${fee.toFixed(2)})`);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Mortgage Break & Refix Calculator</Typography>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Current Loan Amount ($)"
                type="number"
                value={currentLoanAmount}
                onChange={(e) => setCurrentLoanAmount(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Current Fixed Term Expiry Date"
                value={currentFixedExpiryDate}
                onChange={(newValue) => setCurrentFixedExpiryDate(newValue)}
                format="yyyy-MM-dd" // Added date format
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Current Fixed Interest Rate (% p.a.)"
                type="number"
                value={currentFixedRate}
                onChange={(e) => setCurrentFixedRate(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 0, step: "0.01" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="New Interest Rate (% p.a.)"
                type="number"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 0, step: "0.01" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Break Fee (if known, $)"
                type="number"
                value={breakFee}
                onChange={(e) => setBreakFee(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
                helperText="Enter 0 if no fee or unknown and you want to see interest difference only."
              />
            </Grid>

            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button variant="contained" color="primary" onClick={calculateBreakCostOrSaving} sx={{ mt: 2 }}>
                Calculate Break Cost/Saving
              </Button>
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              </Grid>
            )}

            {calculationResult && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>{calculationResult}</Alert>
              </Grid>
            )}
          </Grid>
          <Typography variant="caption" display="block" sx={{ mt: 3, fontStyle: 'italic' }}>
            Note: This calculator provides an estimation based on a simplified interest differential. Actual bank break fees can be calculated differently and may include other factors. Always consult with your bank for an exact break fee calculation.
          </Typography>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
